import { useState } from "react"
import { message, Select, Space, Typography } from "antd"
import {
  ApiHistory,
  ApiPlacementHistory,
  ApiSBreverseBidChange,
} from "../../../api-wrapper/otherHistory/History"
import { usePagedHistory, IHistoryPageQuery } from "../hooks/usePagedHistory"
import useSbSdHistoryColumns from "../hooks/useSbSdHistoryColumns"
import { useTypedSelector } from "../../../hooks/useTypedSelector"

import {
  IPlacementHistoryRecord,
  ISbSdHistoryRecord,
  SbSdCampaignType,
} from "../types/history.types"
import HistoryToolbar from "./HistoryToolbar"
import HistoryStatusSelect from "./HistoryStatusSelect"
import HistoryTable from "./HistoryTable"

const TYPE_OPTIONS = [
  { value: SbSdCampaignType.SPONSORED_BRANDS, label: SbSdCampaignType.SPONSORED_BRANDS },
  { value: SbSdCampaignType.SPONSORED_DISPLAY, label: SbSdCampaignType.SPONSORED_DISPLAY },
]

interface SbSdHistoryProps {
  fixedType?: SbSdCampaignType
}

const formatPlacementValue = (placement: string, value: number | null) =>
  value == null ? null : `${placement}: ${value}%`

const placementHistoryToTargetRow = (row: IPlacementHistoryRecord): ISbSdHistoryRecord => ({
  _id: row._id,
  isRun: row.status as ISbSdHistoryRecord["isRun"],
  campaignName: row.campaignName,
  campaignId: row.campaignId,
  targetId: row.campaignId,
  targetName: row.placement === "Audience" ? "Audience placement" : "Campaign placements",
  oldBid: null,
  bid: null,
  placementsBefore: formatPlacementValue(row.placement, row.oldPercentage),
  placementsAfter:
    formatPlacementValue(row.placement, row.newPercentage) ??
    (row.newAudienceName ? `Audience: ${row.newAudienceName}` : null),
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
})

const SbSdHistory: React.FC<SbSdHistoryProps> = ({ fixedType }) => {
  const [status, setStatus] = useState("")
  const [selectedType, setSelectedType] = useState(SbSdCampaignType.SPONSORED_BRANDS)
  const type = fixedType ?? selectedType
  const columns = useSbSdHistoryColumns()

  const fetchPage = async (query: IHistoryPageQuery) => {
    const targetHistory = await ApiHistory({ ...query, status, type })
    if (type !== SbSdCampaignType.SPONSORED_BRANDS) {
      return targetHistory
    }

    const placementHistory = await ApiPlacementHistory({ ...query, status })
    return {
      ...targetHistory,
      data: [
        ...(targetHistory.data ?? []),
        ...(placementHistory.data ?? []).map(placementHistoryToTargetRow),
      ],
      totalRecords: (targetHistory.totalRecords ?? 0) + (placementHistory.totalRecords ?? 0),
    }
  }

  const { rows, totalRecords, loading, pageNo, perPage, changePage, reload } =
    usePagedHistory<ISbSdHistoryRecord>(fetchPage, [status, type])
  const { dateRange } = useTypedSelector((state) => state.filter)
  const reverseConfirmMessage = `Reverse all ${type} changes for the selected date range?${
    type === SbSdCampaignType.SPONSORED_BRANDS
      ? " SB placement changes shown in this tab are included by day."
      : ""
  }`

  const reverseChanges = async () => {
    if (!rows.length) {
      message.error("No campaigns to reverse")
      return
    }

    try {
      const response = await ApiSBreverseBidChange({
        profileId: localStorage.getItem("profileId") || "",
        from: dateRange.from,
        to: dateRange.to,
        type,
      })
      message.success(response?.message ?? "Changes reversed")
      reload()
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? "Something went wrong")
    }
  }

  return (
    <>
      <HistoryToolbar onReverse={reverseChanges} reverseConfirmMessage={reverseConfirmMessage}>
        {!fixedType && (
          <Space size={4}>
            <Typography.Text type="secondary">Type</Typography.Text>
            <Select value={type} onChange={setSelectedType} options={TYPE_OPTIONS} />
          </Space>
        )}
        <HistoryStatusSelect value={status} onChange={setStatus} />
      </HistoryToolbar>

      <HistoryTable
        columns={columns}
        rows={rows}
        loading={loading}
        pageNo={pageNo}
        perPage={perPage}
        totalRecords={totalRecords}
        onPageChange={changePage}
      />
    </>
  )
}

export default SbSdHistory
